// Uyarlanmış kopya — emsal: beltasapp `serverIssues` (kalemler) + `sistem/line_errors.py` gövdesi.
// Fark: gövde `rows[]` (`{row, field, message}`, sözleşme §2.2); istemci doğrulaması zod şemasıyla.
import type { ZodType } from 'zod'

import { ApiError, type Problem } from '@/lib/problem'

/** Toplu kaydette reddedilen satırların `code` değeri (backend `core/row_errors.py` → 400). */
export const ROW_ERRORS_CODE = 'row_errors'

/** Satır anahtarı → alan → mesaj. `components/grid` `GridIssues` ile aynı biçim. */
export type RowIssues = Record<string, Record<string, string>>

/** Alanı olmayan (satırın tamamına ait) hata bu anahtarla tutulur; ızgara satırın ilk hücresinde gösterir. */
export const ROW_FIELD = '*'

/** `error` 400 `row_errors` ise problem gövdesi, değilse `null`. */
export function rowErrorsProblem(error: unknown): Problem | null {
  if (!(error instanceof ApiError)) return null
  if (error.status !== 400 || error.code !== ROW_ERRORS_CODE) return null
  return error.problem
}

function put(issues: RowIssues, key: string, field: string, message: string): void {
  const row = (issues[key] ??= {})
  // Aynı alana birden çok hata gelirse ilki gösterilir.
  if (!(field in row)) row[field] = message
}

/**
 * Sunucunun `rows[]`'ini ızgara satırlarına dağıtır. `sentKeys[i]` istekte `i`. sırada gönderilen
 * satırın anahtarıdır; `fields` sunucu alanı → ızgara kolonu (eşlemede yoksa aynı ad).
 */
export function rowIssuesFromProblem(
  problem: Problem,
  sentKeys: readonly string[],
  fields: Partial<Record<string, string>> = {},
): RowIssues {
  const issues: RowIssues = {}
  for (const error of problem.rows ?? []) {
    const key = sentKeys[error.row]
    if (key === undefined) continue
    const field = error.field == null ? ROW_FIELD : (fields[error.field] ?? error.field)
    put(issues, key, field, error.message)
  }
  return issues
}

/**
 * Satırları istemcide zod şemasıyla doğrular (sunucuya gitmeden önce); hata yoksa boş nesne.
 * `fields` şema alanı → ızgara kolonu (eşlemede yoksa aynı ad).
 */
export function rowIssuesFromSchema<R>(
  rows: readonly R[],
  schema: ZodType,
  rowKey: (row: R) => string,
  fields: Partial<Record<string, string>> = {},
): RowIssues {
  const issues: RowIssues = {}
  for (const row of rows) {
    const result = schema.safeParse(row)
    if (result.success) continue
    for (const issue of result.error.issues) {
      const path = issue.path.length ? String(issue.path[0]) : null
      const field = path === null ? ROW_FIELD : (fields[path] ?? path)
      put(issues, rowKey(row), field, issue.message)
    }
  }
  return issues
}

/** Hata sayısı (satır başına alan sayısı toplamı). */
export function countRowIssues(issues: RowIssues): number {
  return Object.values(issues).reduce((sum, row) => sum + Object.keys(row).length, 0)
}
